/**
 * v3.15-1104 — CF360 History Tab
 *
 * Renders every prior CF360 cycle for a client, including completion
 * status, completion date, summary notes, and attached document refs.
 * Per JOINT_IMPLEMENTATION_PLAN §2.3.
 *
 * Pulls from `db.cf360_transfers` via a small GET helper (the existing
 * activities surface is the system-of-record for events; this tab
 * groups by cycle so an operator can see the file lifecycle at a
 * glance).
 */
import { useEffect, useState } from 'react';
import { Loader2, FileText, ShieldAlert, CheckCircle2, XCircle, Clock } from 'lucide-react';
import api from '../lib/api';

const StatusIcon = ({ status }) => {
  switch (status) {
    case 'returned':
      return <CheckCircle2 className="h-4 w-4 text-emerald-600" />;
    case 'manual_return':
      return <CheckCircle2 className="h-4 w-4 text-slate-500" />;
    case 'failed':
    case 'cleared':
      return <XCircle className="h-4 w-4 text-rose-500" />;
    case 'pending':
      return <Clock className="h-4 w-4 text-amber-600" />;
    case 'sent':
      return <ShieldAlert className="h-4 w-4 text-amber-600" />;
    default:
      return <FileText className="h-4 w-4 text-slate-400" />;
  }
};

export const CF360HistoryTab = ({ clientId }) => {
  const [transfers, setTransfers] = useState([]);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!clientId) return;
    let mounted = true;
    setLoading(true);
    setErr(null);
    (async () => {
      try {
        // Pull transfers (one row per CF360 cycle).
        const tRes = await api.get(`/clients/${clientId}/cf360-transfers`).catch(() => ({ data: { transfers: [] } }));
        // Pull activities filtered to CF360 types.
        const aRes = await api.get(`/clients/${clientId}/activities`).catch(() => ({ data: { activities: [] } }));
        if (!mounted) return;
        setTransfers(tRes?.data?.transfers || []);
        const acts = (aRes?.data?.activities || aRes?.data || []).filter((a) =>
          (a?.activity_type || '').startsWith('cf360_')
        );
        setActivities(acts);
      } catch (e) {
        if (mounted) setErr(e?.message || 'Failed to load CF360 history');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [clientId]);

  if (loading) {
    return (
      <div data-testid="cf360-history-tab-loading" className="p-6 text-center text-slate-500 text-sm">
        <Loader2 className="h-4 w-4 animate-spin inline mr-2" />
        Loading CF360 history…
      </div>
    );
  }

  if (err) {
    return (
      <div data-testid="cf360-history-tab-error" className="p-4 rounded-md bg-rose-50 border border-rose-200 text-rose-900 text-sm">
        {err}
      </div>
    );
  }

  if (transfers.length === 0 && activities.length === 0) {
    return (
      <div data-testid="cf360-history-tab-empty" className="p-6 text-center text-slate-500 text-sm">
        No CF360 history for this client.
      </div>
    );
  }

  return (
    <div data-testid="cf360-history-tab" className="space-y-4">
      {transfers.length > 0 && (
        <section>
          <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
            Transfer cycles
          </h3>
          <div className="space-y-2">
            {transfers.map((t) => (
              <article
                key={t.transfer_ref || t.id}
                data-testid="cf360-history-row"
                className="rounded-md border border-slate-200 bg-white p-3 text-sm"
              >
                <header className="flex items-start gap-2 justify-between flex-wrap">
                  <div className="flex items-center gap-2">
                    <StatusIcon status={t.status} />
                    <span className="font-semibold text-slate-800 capitalize">
                      {(t.status || '').replace('_', ' ')}
                    </span>
                    {t.completion_status && (
                      <span className="text-xs bg-slate-100 rounded px-1.5 py-0.5 text-slate-700">
                        {t.completion_status}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate-500 font-mono">
                    {t.transfer_ref?.slice(0, 8)}…
                  </div>
                </header>
                <div className="text-xs text-slate-600 mt-2 grid grid-cols-2 gap-x-3 gap-y-0.5">
                  {t.initiated_at && <div><span className="text-slate-400">Initiated:</span> {t.initiated_at.slice(0, 19).replace('T', ' ')}</div>}
                  {t.completed_at && <div><span className="text-slate-400">Completed:</span> {t.completed_at.slice(0, 19).replace('T', ' ')}</div>}
                  {t.initiated_by_user_id && <div><span className="text-slate-400">By:</span> {t.initiated_by_user_id}</div>}
                  {t.cf360_case_id && <div><span className="text-slate-400">CF360 Case:</span> {t.cf360_case_id}</div>}
                </div>
                {t.credit_concerns && (
                  <div className="text-xs text-slate-700 mt-2">
                    <span className="text-slate-400">Credit concerns:</span> <em className="not-italic">{t.credit_concerns}</em>
                  </div>
                )}
                {t.summary_notes_excerpt && (
                  <div className="text-xs text-slate-700 mt-1">
                    <span className="text-slate-400">Summary:</span> {t.summary_notes_excerpt}
                  </div>
                )}
                {t.last_error && (
                  <div className="text-xs text-rose-700 mt-1">
                    <span className="text-rose-400">Note:</span> {t.last_error}
                  </div>
                )}
              </article>
            ))}
          </div>
        </section>
      )}

      {activities.length > 0 && (
        <section>
          <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
            Timeline events
          </h3>
          <ul className="space-y-1.5">
            {activities.map((a) => (
              <li
                key={a.id}
                data-testid="cf360-history-activity"
                className="text-xs text-slate-700 flex items-start gap-2"
              >
                <StatusIcon status={a.activity_type?.replace('cf360_', '')} />
                <div className="flex-1">
                  <span className="font-medium">{a.activity_type?.replace('cf360_', '').replace('_', ' ')}</span>
                  {' '}
                  <span className="text-slate-500">{a.description || ''}</span>
                  <span className="text-slate-400 ml-2">
                    {(a.created_at || '').slice(0, 19).replace('T', ' ')}
                  </span>
                  {a.user_name && (
                    <span className="text-slate-400 ml-2">— {a.user_name}</span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
};

export default CF360HistoryTab;
